/* Which tenant a Time hierarchy tab reads and writes (PRD lph-t35-fe-hierarchy FR-11). */

import { gqlErrorCode } from '@/lib/gql_error'
import type { TenantScope } from '@/components/time/hierarchy/hierarchy-types'
import type { PolicyScopeVars } from './time-off-live'

export type { TenantScope }

export const OWN_SCOPE: TenantScope = { kind: 'own' }

/** The `?scope=` value: `own`, `all`, or one client's tenant id. */
export type ScopeParam = 'own' | 'all' | (string & {})

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * A URL value as a scope. Anything that is not `all` or a UUID is `own`: a hand-edited or stale value never leaves the tab on a
 * scope nobody chose. Whether a UUID names a client the caller manages is the server's live hop check, not this parser's.
 */
export function parseScopeParam(raw: unknown): ScopeParam {
  if (raw === 'all') return 'all'
  return typeof raw === 'string' && UUID_RE.test(raw) ? raw.toLowerCase() : 'own'
}

export function parseScope(raw: unknown): TenantScope {
  const p = parseScopeParam(raw)
  return p === 'own' ? OWN_SCOPE : p === 'all' ? { kind: 'allClients' } : { kind: 'client', tenantId: p }
}

export function scopeParam(scope: TenantScope): ScopeParam {
  return scope.kind === 'own' ? 'own' : scope.kind === 'allClients' ? 'all' : scope.tenantId
}

/** Spread into every read. Own sends neither argument, so the own-tenant tab sends byte-for-byte the requests it always has. */
export function scopeReadVars(scope: TenantScope): PolicyScopeVars {
  if (scope.kind === 'client') return { actingTenantId: scope.tenantId }
  return scope.kind === 'allClients' ? { includeManagedClients: true } : {}
}

/**
 * For an operation that takes `actingTenantId` but not `includeManagedClients` (fork candidates, calendar summaries): the chosen client,
 * else nothing. Under All my clients such a read answers for the caller's own tenant.
 */
export function scopeActingVars(scope: TenantScope): { actingTenantId?: string } {
  return scope.kind === 'client' ? { actingTenantId: scope.tenantId } : {}
}

/**
 * Spread into every write. `null` for All my clients: a write needs exactly one target tenant, so that scope makes no write
 * (the tab hides them; `requireWriteVars` refuses one that slips through).
 */
export function scopeWriteVars(scope: TenantScope): { actingTenantId?: string } | null {
  if (scope.kind === 'allClients') return null
  return scope.kind === 'client' ? { actingTenantId: scope.tenantId } : {}
}

export const ALL_CLIENTS_READ_ONLY = 'Choose one client to make changes.'

export function requireWriteVars(scope: TenantScope): { actingTenantId?: string } {
  const vars = scopeWriteVars(scope)
  if (!vars) throw new Error(ALL_CLIENTS_READ_ONLY)
  return vars
}

export const canWriteInScope = (scope: TenantScope): boolean => scopeWriteVars(scope) !== null

/**
 * The tenant whose rows are "own" for tiering. In a client scope the server answers as that client, so nothing is the caller's
 * own: the client's rows are `MANAGED_CLIENT` (`Client: <name>`) and the agency's are `AGENCY` (fork into the client).
 */
export function tierViewpoint(scope: TenantScope, activeTenantId: string | null | undefined): string | null | undefined {
  return scope.kind === 'client' ? null : activeTenantId
}

/**
 * Typed refusals of a scoped read or write (handover §10). Checked before each scoped tab's own copy (leave policies, calendars).
 * The enrolment review stays own-tenant (PRD-AMEND FR-11), so its copy map is unchanged.
 */
export const SCOPE_ERROR_COPY: Record<string, string> = {
  TT_ACTING_AS_WRITE_FORBIDDEN: 'This session is read-only.',
  TT_CROSS_TENANT_HOP_DENIED: 'You no longer manage this client.',
  TT_ACL_UNAVAILABLE: 'Access could not be checked. Try again.',
}

export function scopeErrorMessage(err: unknown): string | undefined {
  const code = gqlErrorCode(err)
  return code && Object.hasOwn(SCOPE_ERROR_COPY, code) ? SCOPE_ERROR_COPY[code] : undefined
}
